/* eslint-disable react/prop-types */
/* eslint-disable no-unused-vars */
import React, { useEffect, useMemo, useRef, useState } from 'react';
import { Modal, Tooltip, Tag, Empty, Spin, Image, Collapse } from 'antd';
import {
  CheckCircleOutlined,
  WarningOutlined,
  CloseCircleOutlined,
  DownOutlined,
  UpOutlined,
} from '@ant-design/icons';
import dayjs from 'dayjs';
import { queryUserInStoreRecordDetail } from './api';
import './StoreDetailDialog.less';

const getEventCodeName = (eventCode) => {
  const eventMap = {
    ENTER_STORE: '进店',
    TREADMILL: '跑步机',
    PERSONAL_TRAINING: '私教',
    GROUP_CLASS: '团课',
    CHECKOUT: '离店',
  };
  return eventMap[eventCode] || eventCode;
};

const getEventColor = (eventCode) => {
  const colorMap = {
    ENTER_STORE: '#10b981',
    CHECKOUT: '#ef4444',
    TREADMILL: '#8b5cf6',
    GROUP_CLASS: '#f59e0b',
    PERSONAL_TRAINING: '#3b82f6',
  };
  return colorMap[eventCode] || '#909399';
};

const getEventSize = (eventCode) => {
  return ['TREADMILL', 'GROUP_CLASS', 'PERSONAL_TRAINING'].includes(eventCode)
    ? 'large'
    : 'normal';
};

const formatTime = (time) => {
  if (!time) return '-';
  return dayjs(time).format('YYYY-MM-DD HH:mm:ss');
};

const formatRecordDate = (time) => {
  if (!time) return '-';
  return dayjs(time).format('YYYY-MM-DD');
};

const formatClockTime = (time) => {
  if (!time) return '-';
  return dayjs(time).format('HH:mm:ss');
};

const formatDurationSimple = (seconds) => {
  if (!seconds || seconds === 0) return '-';
  const hours = Math.floor(seconds / 3600);
  const minutes = Math.floor((seconds % 3600) / 60);
  const secs = seconds % 60;
  let result = '';
  if (hours > 0) result += `${hours}小时`;
  if (minutes > 0) result += `${minutes}分钟`;
  if (secs > 0) result += `${secs}秒`;
  return result || '-';
};

const getEventEndTime = (event) => {
  if (!event) return 0;
  if (event.endTime) return event.endTime;
  if (event.durationSeconds)
    return event.happenTime + event.durationSeconds * 1000;
  return event.happenTime;
};

const formatEventTimeRange = (event) => {
  if (!event?.happenTime) return '-';
  const start = formatClockTime(event.happenTime);
  const endTime = getEventEndTime(event);
  if (!event.durationSeconds || endTime <= event.happenTime) {
    return start;
  }
  return `${start} - ${formatClockTime(endTime)}`;
};

const hasEventOverlap = (sourceEvent, targetEvent) => {
  if (!sourceEvent || !targetEvent) return false;
  if (sourceEvent === targetEvent) return false;
  const sourceEndTime = getEventEndTime(sourceEvent);
  const targetEndTime = getEventEndTime(targetEvent);
  if (
    sourceEndTime <= sourceEvent.happenTime ||
    targetEndTime <= targetEvent.happenTime
  ) {
    return false;
  }
  return (
    sourceEvent.happenTime < targetEndTime &&
    targetEvent.happenTime < sourceEndTime
  );
};

const getTooltipEvents = (event, events) => {
  if (!event || !Array.isArray(events) || events.length === 0) return [];
  const overlappedEvents = events.filter((e) => hasEventOverlap(event, e));
  if (!overlappedEvents.length) return [];
  return [event, ...overlappedEvents]
    .sort((a, b) => a.happenTime - b.happenTime)
    .map((e, i) => ({
      ...e,
      tooltipKey: `${e.id || e.eventId || e.eventCode || 'event'}-${
        e.happenTime
      }-${i}`,
    }));
};

const getTooltipEventKey = (event) => {
  if (!event) return '';
  return `${event.id || event.eventId || event.eventCode || 'event'}-${
    event.happenTime
  }-${event.endTime || ''}`;
};

const assignTrackTooltipEvents = (trackBlocks, totalDuration) => {
  if (!Array.isArray(trackBlocks) || trackBlocks.length === 0) return [];

  const sortedBlocks = [...trackBlocks].sort(
    (a, b) => a.happenTime - b.happenTime,
  );

  const buildClusterTooltipEvents = (clusterBlocks) => {
    const tooltipEventMap = new Map();
    clusterBlocks.forEach((block) => {
      (block.tooltipEvents || []).forEach((event) => {
        tooltipEventMap.set(getTooltipEventKey(event), event);
      });
      tooltipEventMap.set(getTooltipEventKey(block), block);
    });
    return Array.from(tooltipEventMap.values())
      .sort((a, b) => a.happenTime - b.happenTime)
      .map((e, i) => ({
        ...e,
        tooltipKey: `${getTooltipEventKey(e)}-${i}`,
      }));
  };

  const getRenderWindow = (block) => {
    const visualDuration =
      block.visualDuration ??
      Math.max(0, (block.visualEnd || 0) - (block.visualStart || 0));
    if (block.isLast) {
      return {
        start: Math.max(0, totalDuration - visualDuration),
        end: totalDuration,
      };
    }
    return {
      start: block.visualStart,
      end: Math.min(totalDuration, block.visualStart + visualDuration),
    };
  };

  let currentCluster = [];
  let currentClusterEnd = -Infinity;

  const applyCluster = () => {
    if (!currentCluster.length) return;
    const tooltipEvents = buildClusterTooltipEvents(currentCluster);
    currentCluster.forEach((block) => {
      block.tooltipEvents = tooltipEvents;
    });
  };

  sortedBlocks.forEach((block) => {
    const renderWindow = getRenderWindow(block);
    if (!currentCluster.length) {
      currentCluster = [block];
      currentClusterEnd = renderWindow.end;
      return;
    }
    if (renderWindow.start <= currentClusterEnd) {
      currentCluster.push(block);
      currentClusterEnd = Math.max(currentClusterEnd, renderWindow.end);
      return;
    }
    applyCluster();
    currentCluster = [block];
    currentClusterEnd = renderWindow.end;
  });

  applyCluster();
  return trackBlocks;
};

const StoreDetailDialog = ({ visible, onClose, record }) => {
  const [detailData, setDetailData] = useState(null);
  const [loadingDetail, setLoadingDetail] = useState(false);
  const [isTimelineExpanded, setIsTimelineExpanded] = useState(true);
  const timelineHeaderRef = useRef(null);

  const dialogTitle = useMemo(() => {
    if (!detailData) return '事件详情';
    const { eventRefType, eventCode } = detailData;
    if (eventRefType === 'IN_STORE_RECORD') return '到店训练 事件详情';
    if (eventRefType === 'IN_STORE_EVENT')
      return `${getEventCodeName(eventCode)} 事件详情`;
    if (eventRefType === 'USER_SUPPLEMENT') return '补卡 事件详情';
    return '事件详情';
  }, [detailData]);

  const titleStyle = useMemo(() => {
    if (!detailData) return {};
    const { eventRefType, eventCode } = detailData;
    if (eventRefType === 'IN_STORE_RECORD') return { color: '#1890ff' };
    if (eventRefType === 'IN_STORE_EVENT') {
      const colorMap = {
        TREADMILL: '#10b981',
        GROUP_CLASS: '#f59e0b',
        PERSONAL_TRAINING: '#3b82f6',
      };
      return { color: colorMap[eventCode] || '#1890ff' };
    }
    if (eventRefType === 'USER_SUPPLEMENT') return { color: '#faad14' };
    return {};
  }, [detailData]);

  const ganttTracks = useMemo(() => {
    if (!detailData?.events?.length) return [];
    const events = detailData.events;
    const minTime = Math.min(...events.map((e) => e.happenTime));
    const maxTime = Math.max(...events.map((e) => getEventEndTime(e)));
    const totalDuration = maxTime - minTime || 1;

    const tracksMap = {};
    events.forEach((event) => {
      const startOffset = Math.max(0, event.happenTime - minTime);
      const visualDuration = event.durationSeconds
        ? event.durationSeconds * 1000
        : totalDuration * 0.015;

      let leftPct = (startOffset / totalDuration) * 100;
      let widthPct = (visualDuration / totalDuration) * 100;
      const isLast = startOffset >= totalDuration - 1000;

      if (isLast) {
        leftPct = 100;
        widthPct = 0;
      } else if (leftPct + widthPct > 100) {
        widthPct = 100 - leftPct;
      }

      if (!tracksMap[event.eventCode]) {
        tracksMap[event.eventCode] = {
          id: event.eventCode,
          name: getEventCodeName(event.eventCode),
          blocks: [],
        };
      }

      tracksMap[event.eventCode].blocks.push({
        ...event,
        left: leftPct,
        width: widthPct,
        isLast,
        color: getEventColor(event.eventCode),
        tooltipEvents: getTooltipEvents(event, events),
        visualDuration,
        visualStart: startOffset,
        visualEnd: startOffset + visualDuration,
      });
    });

    return Object.values(tracksMap).map((track) => ({
      ...track,
      blocks: assignTrackTooltipEvents(track.blocks, totalDuration),
    }));
  }, [detailData]);

  const ganttTimeRange = useMemo(() => {
    if (!detailData?.events?.length) return { start: '', end: '' };
    const events = detailData.events;
    const minTime = Math.min(...events.map((e) => e.happenTime));
    const maxTime = Math.max(...events.map((e) => getEventEndTime(e)));
    return {
      start: dayjs(minTime).format('HH:mm'),
      end: dayjs(maxTime).format('HH:mm'),
    };
  }, [detailData]);

  const loadDetail = async () => {
    if (!record?.eventId) return;
    setLoadingDetail(true);
    try {
      const res = await queryUserInStoreRecordDetail({
        requestData: {
          eventId: record.eventId,
        },
      });

      console.log('res', res);
      if (res?.data) {
        const {
          eventRefType,
          inStoreRecordDetail,
          inStoreEventMetadataDetail,
          userInStoreSupplementDetail,
          eventId,
        } = res?.data || {};
        if (eventRefType === 'IN_STORE_RECORD' && inStoreRecordDetail) {
          setDetailData({
            ...inStoreRecordDetail,
            eventId,
            eventRefType,
          });
        } else if (
          eventRefType === 'IN_STORE_EVENT' &&
          inStoreEventMetadataDetail
        ) {
          setDetailData({
            ...inStoreEventMetadataDetail,
            eventId,
            eventRefType,
          });
        } else if (
          eventRefType === 'USER_SUPPLEMENT' &&
          userInStoreSupplementDetail
        ) {
          setDetailData({
            ...userInStoreSupplementDetail,
            eventId,
            eventRefType,
          });
        } else {
          console.warn('未处理的事件类型:', eventRefType);
          setDetailData(null);
        }
      }
    } catch (error) {
      console.error('获取详情异常:', error);
      setDetailData(null);
    } finally {
      setLoadingDetail(false);
    }
  };

  useEffect(() => {
    if (visible) {
      setIsTimelineExpanded(true);
      loadDetail();
    } else {
      setDetailData(null);
    }
  }, [visible]);

  const toggleTimeline = () => {
    setIsTimelineExpanded((prev) => {
      const next = !prev;
      if (next) {
        setTimeout(() => {
          timelineHeaderRef.current?.scrollIntoView({
            behavior: 'smooth',
            block: 'start',
          });
        }, 100);
      }
      return next;
    });
  };

  const renderTooltipContent = (block) => (
    <div className="tooltip-content">
      {block.tooltipEvents?.length ? (
        block.tooltipEvents.map((tooltipEvent, eventIdx) => (
          <div
            key={tooltipEvent.tooltipKey || eventIdx}
            className="tooltip-event"
          >
            <div className="tooltip-title">
              {tooltipEvent.eventName ||
                getEventCodeName(tooltipEvent.eventCode)}
              {tooltipEvent.isCurrentStore === false && (
                <span className="tooltip-sub-label">(不在当前门店)</span>
              )}
            </div>
            <div className="tooltip-time">
              {formatEventTimeRange(tooltipEvent)}
            </div>
          </div>
        ))
      ) : (
        <>
          <div className="tooltip-title">
            {getEventCodeName(block.eventCode)}
            {block.isCurrentStore === false && (
              <span className="tooltip-sub-label">(不在当前门店)</span>
            )}
          </div>
          <div className="tooltip-time">{formatEventTimeRange(block)}</div>
        </>
      )}
    </div>
  );

  const renderStoreHeader = () => {
    if (!detailData) return null;

    if (detailData.eventRefType === 'IN_STORE_RECORD') {
      return (
        <div className="store-header">
          <div className="header-main">
            <div className="store-name">
              {detailData.storeName || '未知门店'}
            </div>
          </div>
          <div className="store-meta">
            <div className="meta-row">
              <span className="meta-label">到店日期：</span>
              <span className="meta-value">
                {formatRecordDate(detailData.startTime)}
              </span>
            </div>
            {detailData.totalDurationSeconds && (
              <div className="meta-row">
                <span className="meta-label">总在店时长：</span>
                <span className="meta-value">
                  {formatDurationSimple(detailData.totalDurationSeconds)}
                </span>
              </div>
            )}
            <div className="meta-row full-width">
              <span className="meta-label">在店时段：</span>
              <span className="meta-value">
                {formatTime(detailData.startTime)} -{' '}
                {formatTime(detailData.endTime)}
              </span>
            </div>
            {detailData.trustedDurationSeconds !== undefined && (
              <div className="meta-row">
                <span className="meta-label">可信时长：</span>
                <span className="meta-value highlight">
                  {formatDurationSimple(detailData.trustedDurationSeconds)}
                </span>
              </div>
            )}
            {detailData.riskDurationSeconds !== undefined &&
              detailData.riskDurationSeconds > 0 && (
                <div className="meta-row">
                  <span className="meta-label">风险时长：</span>
                  <span className="meta-value warning">
                    {formatDurationSimple(detailData.riskDurationSeconds)}
                  </span>
                </div>
              )}
            {detailData.riskDescription && (
              <div className="meta-row full-width">
                <span className="meta-label">风险提示：</span>
                <span className="meta-value warning">
                  {detailData.riskDescription}
                </span>
              </div>
            )}
          </div>
        </div>
      );
    }

    if (detailData.eventRefType === 'IN_STORE_EVENT') {
      return (
        <div className="store-header">
          <div className="header-main">
            <div className="store-name">
              {detailData.storeName || '未知门店'}
            </div>
          </div>
          <div className="store-meta">
            <div className="meta-row">
              <span className="meta-label">事件日期：</span>
              <span className="meta-value">
                {formatRecordDate(detailData.happenTime)}
              </span>
            </div>
            {detailData.durationSeconds && (
              <div className="meta-row">
                <span className="meta-label">持续时长：</span>
                <span className="meta-value">
                  {formatDurationSimple(detailData.durationSeconds)}
                </span>
              </div>
            )}
            <div className="meta-row full-width">
              <span className="meta-label">事件时段：</span>
              <span className="meta-value">
                {formatTime(detailData.happenTime)} -{' '}
                {formatTime(detailData.endTime)}
              </span>
            </div>
            {detailData.riskControlDesc && (
              <div className="meta-row full-width">
                <span className="meta-label">风控说明：</span>
                <span className="meta-value warning">
                  {detailData.riskControlDesc}
                </span>
              </div>
            )}
          </div>
        </div>
      );
    }

    if (detailData.eventRefType === 'USER_SUPPLEMENT') {
      return (
        <div className="store-header supplement-section">
          <div className="header-main">
            <div className="store-name">
              {detailData.storeName || '未知门店'}
            </div>
          </div>
          <div className="store-meta">
            <div className="meta-row">
              <span className="meta-label">补卡日期：</span>
              <span className="meta-value">
                {formatRecordDate(detailData.startTime)}
              </span>
            </div>
            {detailData.totalDurationSeconds && (
              <div className="meta-row">
                <span className="meta-label">在店时长：</span>
                <span className="meta-value">
                  {formatDurationSimple(detailData.totalDurationSeconds)}
                </span>
              </div>
            )}
            <div className="meta-row full-width">
              <span className="meta-label">补卡时段：</span>
              <span className="meta-value">
                {formatTime(detailData.startTime)} -{' '}
                {formatTime(detailData.endTime)}
              </span>
            </div>
          </div>
          {(detailData.supplementDescription ||
            (detailData.supplementImages &&
              detailData.supplementImages.length > 0)) && (
            <div className="supplement-extra">
              {detailData.supplementDescription && (
                <div className="meta-row full-width">
                  <span className="meta-label">补卡说明：</span>
                  <span className="meta-value supplement-desc">
                    {detailData.supplementDescription}
                  </span>
                </div>
              )}
              {detailData.supplementImages &&
                detailData.supplementImages.length > 0 && (
                  <div className="meta-row full-width">
                    <span className="meta-label">补卡图片：</span>
                    <div className="image-grid">
                      <Image.PreviewGroup items={detailData.supplementImages}>
                        {detailData.supplementImages.map((img, index) => (
                          <Image
                            key={index}
                            src={img}
                            width={80}
                            height={80}
                            className="grid-image"
                          />
                        ))}
                      </Image.PreviewGroup>
                    </div>
                  </div>
                )}
            </div>
          )}
        </div>
      );
    }

    return null;
  };

  const showTimeline =
    detailData?.eventRefType === 'IN_STORE_RECORD' ||
    detailData?.eventRefType === 'USER_SUPPLEMENT';

  return (
    <Modal
      open={visible}
      onCancel={onClose}
      title={<span style={titleStyle}>{dialogTitle}</span>}
      width={800}
      footer={null}
      destroyOnClose
      centered
      className="store-detail-dialog"
    >
      <Spin spinning={loadingDetail}>
        {detailData && (
          <div className="store-detail-content">
            {renderStoreHeader()}

            {showTimeline && (
              <div className="timeline-container">
                <div className="timeline-title">到店活动轨迹</div>

                {ganttTracks.length > 0 && (
                  <div className="visual-timeline-wrapper">
                    <div className="gantt-chart">
                      <div className="gantt-header">
                        <span className="time-label">
                          {ganttTimeRange.start}
                        </span>
                        <span className="time-label">{ganttTimeRange.end}</span>
                      </div>
                      <div className="gantt-body">
                        {ganttTracks.map((track) => (
                          <div key={track.id} className="gantt-track">
                            <div className="track-name">{track.name}</div>
                            <div className="track-bars">
                              {track.blocks.map((block, idx) => (
                                <Tooltip
                                  key={idx}
                                  placement="top"
                                  title={renderTooltipContent(block)}
                                >
                                  <div
                                    className={`track-block${
                                      !block.durationSeconds || block.isLast
                                        ? ' instant-event'
                                        : ''
                                    }${
                                      block.isCurrentStore === false
                                        ? ' not-current-block'
                                        : ''
                                    }`}
                                    style={
                                      block.isLast
                                        ? {
                                            right: 0,
                                            width: 4,
                                            backgroundColor: block.color,
                                            position: 'absolute',
                                          }
                                        : {
                                            left: `${block.left}%`,
                                            width: `${block.width}%`,
                                            backgroundColor: block.color,
                                            position: 'absolute',
                                          }
                                    }
                                  >
                                    {block.confidenceLevel === 'low' && (
                                      <div className="low-confidence-stripe" />
                                    )}
                                    {block.isCurrentStore === false && (
                                      <div className="not-current-stripe" />
                                    )}
                                  </div>
                                </Tooltip>
                              ))}
                            </div>
                          </div>
                        ))}
                      </div>
                    </div>
                  </div>
                )}

                {detailData.events && detailData.events.length > 0 ? (
                  <>
                    <div
                      className="timeline-collapse-header"
                      ref={timelineHeaderRef}
                      onClick={toggleTimeline}
                    >
                      <div
                        className="section-subtitle"
                        style={{ marginBottom: 0 }}
                      >
                        事件明细记录
                      </div>
                      <div className="toggle-btn">
                        <span>
                          {isTimelineExpanded ? '收起明细' : '查看明细'}
                        </span>
                        {isTimelineExpanded ? <UpOutlined /> : <DownOutlined />}
                      </div>
                    </div>

                    {isTimelineExpanded && (
                      <div className="timeline-collapse-content">
                        <div className="custom-timeline">
                          {detailData.events.map((event, index) => (
                            <div
                              key={event.id || index}
                              className="timeline-item"
                            >
                              <div className="timeline-item-tail">
                                <div
                                  className={`timeline-item-dot ${
                                    getEventSize(event.eventCode) === 'large'
                                      ? 'dot-large'
                                      : 'dot-normal'
                                  }`}
                                  style={{
                                    backgroundColor: getEventColor(
                                      event.eventCode,
                                    ),
                                  }}
                                />
                              </div>
                              <div className="timeline-item-body">
                                <div className="timeline-item-time">
                                  {formatTime(event.happenTime)}
                                </div>
                                <div
                                  className={`timeline-event-content${
                                    event.isCurrentStore === false
                                      ? ' not-current-store'
                                      : ''
                                  }${
                                    event.isCurrentStore !== false &&
                                    event.isOverlapped === true
                                      ? ' overlapped-event'
                                      : ''
                                  }`}
                                >
                                  <div className="event-header">
                                    <span className="event-name">
                                      {event.eventName ||
                                        getEventCodeName(event.eventCode)}
                                      {event.isCurrentStore === false && (
                                        <span className="not-current-label">
                                          (不在当前门店)
                                        </span>
                                      )}
                                      {event.isCurrentStore !== false &&
                                        event.isOverlapped === true && (
                                          <span className="overlapped-label">
                                            (存在时间重叠)
                                          </span>
                                        )}
                                    </span>
                                  </div>

                                  {event.durationSeconds &&
                                    event.durationSeconds > 0 && (
                                      <div className="event-details">
                                        <div className="detail-row">
                                          <span className="detail-label">
                                            持续时间：
                                          </span>
                                          <span className="detail-value">
                                            {formatDurationSimple(
                                              event.durationSeconds,
                                            )}
                                          </span>
                                        </div>
                                        {event.endTime && (
                                          <div className="detail-row">
                                            <span className="detail-label">
                                              结束时间：
                                            </span>
                                            <span className="detail-value">
                                              {formatTime(event.endTime)}
                                            </span>
                                          </div>
                                        )}
                                      </div>
                                    )}

                                  {event.confidenceLevel && (
                                    <div
                                      className={`confidence-box confidence-${event.confidenceLevel}`}
                                    >
                                      <div className="confidence-header">
                                        <div className="confidence-title">
                                          {event.confidenceLevel === 'high' && (
                                            <CheckCircleOutlined
                                              style={{ color: '#10b981' }}
                                            />
                                          )}
                                          {event.confidenceLevel ===
                                            'medium' && (
                                            <WarningOutlined
                                              style={{ color: '#f59e0b' }}
                                            />
                                          )}
                                          {event.confidenceLevel === 'low' && (
                                            <CloseCircleOutlined
                                              style={{ color: '#ef4444' }}
                                            />
                                          )}
                                          <span>置信评估</span>
                                        </div>
                                        <div
                                          className={`confidence-score score-${event.confidenceLevel}`}
                                        >
                                          {event.confidenceScore}%
                                        </div>
                                      </div>
                                      <div className="confidence-desc">
                                        {event.confidenceDesc}
                                      </div>
                                    </div>
                                  )}
                                </div>
                              </div>
                            </div>
                          ))}
                        </div>
                      </div>
                    )}
                  </>
                ) : (
                  <Empty
                    description="暂无事件数据"
                    image={Empty.PRESENTED_IMAGE_SIMPLE}
                  />
                )}
              </div>
            )}
          </div>
        )}
      </Spin>
    </Modal>
  );
};

export default StoreDetailDialog;
